import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank,
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import debounce from '@mui/material/utils/debounce';
import { useEffect, useMemo, useState } from 'react';
import type { ILegalTransaction } from '../../Interfaces/ILegalTransaction';
import type { ISearchOption } from '../../Interfaces/ISearchOption';
import { SearchType } from '../../Interfaces/ISearchType';
import { useSearchLegalTransactions } from '../../hooks/reactQuerys/LegalTransactionQueries';

const SEARCH_OPTIONS: ISearchOption[] = [
  { SearchOptionName: 'Titel', SearchOptionKey: 'Title' },
  {
    SearchOptionName: 'Vorgangsnummer',
    SearchOptionKey: 'TransactionNumberEntry',
  },
  { SearchOptionName: 'Urkundennummer', SearchOptionKey: 'RecordNumberEntry' },
];

const PAGE_SIZE = 25;
const PAGE = 1;

interface IProps {
  unmount: () => void;
  onAdd: (legalTransactions: ILegalTransaction[]) => void;
  // Pass an initial array of pre-selected options.
  preSelectedLegalTransactions?: ILegalTransaction[];
}

export const AddLegalTransactionsDialog = ({
  unmount,
  onAdd,
  preSelectedLegalTransactions = [],
}: IProps) => {
  const [inputValue, setInputValue] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(PAGE);
  const [size] = useState(PAGE_SIZE);
  const [allLegalTransactions, setAllLegalTransactions] = useState<
    ILegalTransaction[]
  >([]);
  // Initialize the selected state with the passed-in pre-selected items.
  const [selectedLegalTransactions, setSelectedLegalTransactions] = useState<
    ILegalTransaction[]
  >(preSelectedLegalTransactions);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((newValue: string) => {
        setPage(PAGE);
        setSearch(newValue.toLowerCase());
      }, 500),
    []
  );

  const searchTerm = useMemo(
    () =>
      SEARCH_OPTIONS.reduce(
        (acc, o) => acc + `${o.SearchOptionKey}:${search};`,
        ''
      ),
    [search]
  );

  const uri = useMemo(() => {
    return search
      ? `search=${searchTerm}&searchtype=${SearchType.INCLUDES}&page=${page}&rowsPerPage=${size}`
      : `page=${page}&rowsPerPage=${size}`;
  }, [search, searchTerm, page, size]);

  const {
    legalTransactions,
    isLoadingLegalTransactions,
    abortSearchLegalTransactions,
  } = useSearchLegalTransactions(uri);

  useEffect(() => {
    if (page === 1) {
      setAllLegalTransactions(legalTransactions || []);
    } else {
      setAllLegalTransactions(prev => [...prev, ...(legalTransactions || [])]);
    }
  }, [legalTransactions, page]);

  const handleListboxScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >=
      listboxNode.scrollHeight - 1
    ) {
      if (!isLoadingLegalTransactions && legalTransactions?.length === size) {
        abortSearchLegalTransactions();
        setPage(prevPage => prevPage + 1);
      }
    }
  };

  const handleInputChange = (_: any, newValue: string) => {
    setInputValue(newValue);
    abortSearchLegalTransactions();
    debouncedSetSearch(newValue);
  };

  // Merge selected and fetched options so that if a selected option isn't in the fetched list it still appears.
  const mergedOptions = useMemo(() => {
    const selectedIds = new Set(
      selectedLegalTransactions.map(t => t.idLegalTransaction)
    );
    return [
      ...selectedLegalTransactions,
      ...allLegalTransactions.filter(
        t => !selectedIds.has(t.idLegalTransaction)
      ),
    ];
  }, [selectedLegalTransactions, allLegalTransactions]);

  return (
    <Dialog onClose={unmount} open={true} fullWidth maxWidth='sm'>
      <DialogTitle>Rechtsgeschäfte</DialogTitle>
      <DialogContent>
        <Autocomplete
          multiple
          fullWidth
          sx={{ pt: 0.75 }}
          value={selectedLegalTransactions}
          options={mergedOptions}
          disableCloseOnSelect
          loading={isLoadingLegalTransactions}
          filterOptions={x => x}
          getOptionLabel={option =>
            option.Title || 'Unbenanntes Rechtsgeschäft'
          }
          isOptionEqualToValue={(option, value) =>
            option.idLegalTransaction === value.idLegalTransaction
          }
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onChange={(_, value) => {
            setSelectedLegalTransactions(value);
          }}
          loadingText='Lade Rechtsgeschäfte...'
          ListboxProps={{
            onScroll: handleListboxScroll,
          }}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.idLegalTransaction}>
              <Checkbox
                icon={<CheckBoxOutlineBlank fontSize='small' />}
                checkedIcon={<CheckBoxIcon fontSize='small' />}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant='body1'>
                    {option.Title || 'Unbenanntes Rechtsgeschäft'}
                  </Typography>
                </Box>
                <Typography variant='body2'>
                  {option.LegalTransactionState}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label='Rechtsgeschäfte'
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoadingLegalTransactions ? (
                      <CircularProgress color='inherit' size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={unmount}>
          Abbrechen
        </Button>
        <Button
          onClick={() => onAdd(selectedLegalTransactions)}
          variant='contained'
        >
          Übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
};
